import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBlog } from '../context/BlogContext';
import { motion } from 'framer-motion';

const icons = {
  home: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  philosophy: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  ),
  about: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M20 21c0-4.418-3.582-8-8-8s-8 3.582-8 8" />
    </svg>
  ),
  journals: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  dashboard: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  instagram: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  ),
  linkedin: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" />
    </svg>
  ),
  email: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  expand: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  collapse: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  close: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  twitter: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4l11.733 16h4.267l-11.733-16z" /><path d="M4 20l6.768-6.768" /><path d="M20 4l-6.768 6.768" />
    </svg>
  ),
};

const navItems = [
  { to: '/', label: 'Home', icon: icons.home },
  { to: '/#journals', label: 'Journals', icon: icons.journals, isAnchor: true },
  { to: '/philosophy', label: 'Philosophy', icon: icons.philosophy },
  { to: '/about', label: 'About Us', icon: icons.about },
];

const socialDefs = [
  { key: 'instagram', label: 'Instagram', icon: icons.instagram },
  { key: 'linkedin', label: 'LinkedIn', icon: icons.linkedin },
  { key: 'twitter', label: 'Twitter / X', icon: icons.twitter },
  { key: 'email', label: 'Email', icon: icons.email },
];

const iconBtnStyle = {
  width: '40px', height: '40px', borderRadius: '10px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: 'var(--text-secondary)', cursor: 'pointer',
  transition: 'all 0.2s', background: 'transparent', border: 'none',
  fontFamily: 'var(--font-sans)',
};

const iconBtnActiveStyle = {
  ...iconBtnStyle,
  background: 'var(--bg-body)', color: 'var(--text-primary)',
};

export default function Sidebar({ open, setOpen, isMobile = false, mobileOpen = false }) {
  const { user, logout } = useAuth();
  const { siteSettings } = useBlog();
  const navigate = useNavigate();
  const socialLinks = siteSettings?.socialLinks || {};
  const brandLogo = siteSettings?.brandLogo || '';

  const getSocialHref = (key) => {
    const val = socialLinks[key] || '';
    if (key === 'email' && val && !val.startsWith('mailto:')) return `mailto:${val}`;
    return val;
  };

  const handleNavClick = (item, e) => {
    if (item.isAnchor) {
      e.preventDefault();
      if (isMobile) setOpen(false);
      if (window.location.pathname === '/') {
        document.getElementById('journals')?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => {
          document.getElementById('journals')?.scrollIntoView({ behavior: 'smooth' });
        }, 400);
      }
    } else if (isMobile) {
      setOpen(false);
    }
  };

  // On mobile, always render expanded sidebar as overlay
  if (isMobile) {
    return (
      <motion.aside
        className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}
        initial={false}
        animate={{ x: mobileOpen ? 0 : -260 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        style={{
          width: '260px',
          position: 'fixed', top: 0, left: 0, height: '100vh',
          background: 'var(--bg-sidebar)',
          borderRight: '1px solid var(--border-subtle)',
          padding: '2rem 1.5rem',
          display: 'flex', flexDirection: 'column',
          zIndex: 100,
          overflow: 'visible',
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Brand + close */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.75rem' }}>
            <NavLink to="/" onClick={() => setOpen(false)} style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                {brandLogo ? (
                  <img src={brandLogo} alt="UMI" style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'contain' }} />
                ) : (
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--bg-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-serif)', fontSize: '0.9rem', fontWeight: 600 }}>U</div>
                )}
                <div>
                  <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1rem', fontWeight: 600, lineHeight: 1.2 }}>UMI</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)' }}>The Mushroom Understory</div>
                </div>
              </div>
            </NavLink>
            <button
              onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px' }}
              aria-label="Close menu"
            >
              {icons.close}
            </button>
          </div>

          {/* Nav */}
          <nav style={{ marginBottom: '1.5rem' }}>
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} onClick={(e) => handleNavClick(item, e)} style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: '0.65rem',
                padding: '0.5rem 0.75rem', marginBottom: '0.15rem', borderRadius: '10px',
                fontSize: '0.88rem', color: (isActive && !item.isAnchor) ? 'var(--text-primary)' : 'var(--text-secondary)',
                background: (isActive && !item.isAnchor) ? 'var(--bg-body)' : 'transparent',
                fontWeight: (isActive && !item.isAnchor) ? 500 : 400, transition: 'all 0.2s',
              })}>
                {item.icon}<span>{item.label}</span>
              </NavLink>
            ))}
            {user?.role === 'admin' && (
              <NavLink to="/admin" onClick={() => setOpen(false)} style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: '0.65rem',
                padding: '0.5rem 0.75rem', marginBottom: '0.15rem', borderRadius: '10px',
                fontSize: '0.88rem', color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                background: isActive ? 'var(--bg-body)' : 'transparent',
                fontWeight: isActive ? 500 : 400, transition: 'all 0.2s',
              })}>
                {icons.dashboard}<span>Dashboard</span>
              </NavLink>
            )}
          </nav>

          {/* Social */}
          <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>Find us</div>
          {socialDefs.map((item) => {
            const href = getSocialHref(item.key);
            const inner = (<>{item.icon}<span>{item.label}</span></>);
            const style = {
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: '0.4rem 0.75rem', fontSize: '0.85rem',
              color: href ? 'var(--text-secondary)' : 'var(--text-tertiary)',
              cursor: href ? 'pointer' : 'default', opacity: href ? 1 : 0.5,
            };
            return href ? (
              <a key={item.key} href={href} target="_blank" rel="noopener noreferrer" style={style}>{inner}</a>
            ) : (
              <div key={item.key} style={style}>{inner}</div>
            );
          })}

          <div style={{ flex: 1 }} />

          {/* User section */}
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '1rem', marginTop: '1rem' }}>
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                {user.picture ? (
                  <img src={user.picture} alt="" style={{ width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0 }} referrerPolicy="no-referrer" />
                ) : (
                  <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'var(--bg-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600, flexShrink: 0 }}>{(user.displayName || 'U')[0]}</div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.displayName}</div>
                  <button onClick={() => { logout(); setOpen(false); }} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'var(--font-sans)', padding: 0 }}>Sign out</button>
                </div>
              </div>
            ) : (
              <NavLink to="/login" onClick={() => setOpen(false)} style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 500 }}>Sign in</NavLink>
            )}
          </div>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', marginTop: '0.75rem' }}>Copyright 2026 UMI Understory</div>
        </div>
      </motion.aside>
    );
  }

  /* ---- Desktop: Collapsed icon strip ---- */
  if (!open) {
    return (
      <motion.aside
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        style={{
          width: '68px', background: 'var(--bg-sidebar)',
          borderRight: '1px solid var(--border-subtle)',
          position: 'fixed', top: 0, left: 0, height: '100vh',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '1.25rem 0', zIndex: 100, gap: '0.25rem',
          overflow: 'visible',
        }}
      >
        <div style={{ marginBottom: '0.5rem', position: 'relative' }}>
          {brandLogo ? (
            <img src={brandLogo} alt="UMI" style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'contain' }} />
          ) : (
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--bg-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-serif)', fontSize: '0.9rem', fontWeight: 600 }}>U</div>
          )}
        </div>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setOpen(true)}
          style={{
            position: 'absolute', top: '1.5rem', right: '-12px',
            width: '24px', height: '24px', borderRadius: '50%',
            background: 'var(--text-primary)', color: 'var(--bg-body)',
            border: '2px solid var(--bg-body)',
            cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 101,
          }}
          aria-label="Expand sidebar"
        >{icons.expand}</motion.button>

        <div style={{ height: '1rem' }} />

        {navItems.map((item) => (
          <NavLink key={item.to} to={item.to} title={item.label} onClick={(e) => handleNavClick(item, e)}>
            {({ isActive }) => (
              <div style={isActive && !item.isAnchor ? iconBtnActiveStyle : iconBtnStyle}>{item.icon}</div>
            )}
          </NavLink>
        ))}
        {user?.role === 'admin' && (
          <NavLink to="/admin" title="Dashboard">
            {({ isActive }) => (
              <div style={isActive ? iconBtnActiveStyle : iconBtnStyle}>{icons.dashboard}</div>
            )}
          </NavLink>
        )}

        <div style={{ height: '1.5rem' }} />

        {socialDefs.map((item) => {
          const href = getSocialHref(item.key);
          return href ? (
            <a key={item.key} href={href} target="_blank" rel="noopener noreferrer" title={item.label} style={iconBtnStyle}>{item.icon}</a>
          ) : (
            <div key={item.key} style={{ ...iconBtnStyle, opacity: 0.35 }} title={item.label}>{item.icon}</div>
          );
        })}

        <div style={{ flex: 1 }} />

        {user ? (
          <button onClick={logout} title="Sign out" style={{ ...iconBtnStyle, fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        ) : (
          <NavLink to="/login" title="Sign in" style={{ ...iconBtnStyle, color: 'var(--text-primary)' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" />
            </svg>
          </NavLink>
        )}
      </motion.aside>
    );
  }

  /* ---- Desktop: Expanded full sidebar ---- */
  return (
    <motion.aside
      className="sidebar"
      initial={{ width: 68 }}
      animate={{ width: 240 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.75rem' }}>
          <NavLink to="/" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              {brandLogo ? (
                <img src={brandLogo} alt="UMI" style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'contain' }} />
              ) : (
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--bg-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-serif)', fontSize: '0.9rem', fontWeight: 600 }}>U</div>
              )}
              <div>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1rem', fontWeight: 600, lineHeight: 1.2 }}>UMI</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)' }}>The Mushroom Understory</div>
              </div>
            </div>
          </NavLink>
        </div>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setOpen(false)}
          style={{
            position: 'absolute', top: '2rem', right: '-12px',
            width: '24px', height: '24px', borderRadius: '50%',
            background: 'var(--text-primary)', color: 'var(--bg-body)',
            border: '2px solid var(--bg-body)',
            cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 101,
          }}
          aria-label="Collapse sidebar"
        >{icons.collapse}</motion.button>

        <nav style={{ marginBottom: '1.5rem' }}>
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} onClick={(e) => handleNavClick(item, e)} style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: '0.5rem 0.75rem', marginBottom: '0.15rem', borderRadius: '10px',
              fontSize: '0.88rem', color: (isActive && !item.isAnchor) ? 'var(--text-primary)' : 'var(--text-secondary)',
              background: (isActive && !item.isAnchor) ? 'var(--bg-body)' : 'transparent',
              fontWeight: (isActive && !item.isAnchor) ? 500 : 400, transition: 'all 0.2s',
            })}>
              {item.icon}<span>{item.label}</span>
            </NavLink>
          ))}
          {user?.role === 'admin' && (
            <NavLink to="/admin" style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: '0.5rem 0.75rem', marginBottom: '0.15rem', borderRadius: '10px',
              fontSize: '0.88rem', color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              background: isActive ? 'var(--bg-body)' : 'transparent',
              fontWeight: isActive ? 500 : 400, transition: 'all 0.2s',
            })}>
              {icons.dashboard}<span>Dashboard</span>
            </NavLink>
          )}
        </nav>

        <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>Find us</div>
        {socialDefs.map((item) => {
          const href = getSocialHref(item.key);
          const inner = (<>{item.icon}<span>{item.label}</span></>);
          const style = {
            display: 'flex', alignItems: 'center', gap: '0.65rem',
            padding: '0.4rem 0.75rem', fontSize: '0.85rem',
            color: href ? 'var(--text-secondary)' : 'var(--text-tertiary)',
            cursor: href ? 'pointer' : 'default', opacity: href ? 1 : 0.5,
          };
          return href ? (
            <a key={item.key} href={href} target="_blank" rel="noopener noreferrer" style={style}>{inner}</a>
          ) : (
            <div key={item.key} style={style}>{inner}</div>
          );
        })}

        <div style={{ flex: 1 }} />

        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '1rem', marginTop: '1rem' }}>
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              {user.picture ? (
                <img src={user.picture} alt="" style={{ width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0 }} referrerPolicy="no-referrer" />
              ) : (
                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'var(--bg-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600, flexShrink: 0 }}>{(user.displayName || 'U')[0]}</div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.displayName}</div>
                <button onClick={logout} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'var(--font-sans)', padding: 0 }}>Sign out</button>
              </div>
            </div>
          ) : (
            <NavLink to="/login" style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 500 }}>Sign in</NavLink>
          )}
        </div>
        <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', marginTop: '0.75rem' }}>Copyright 2026 UMI Understory</div>
      </div>
    </motion.aside>
  );
}
